import React from 'react';
import Link from 'next/link';
import styles from './LayoutFooter.module.scss';
import { Media } from 'react-breakpoints';
import { Logo } from '../header/components/Logo';
import { Row, Col } from 'antd';
import {
  LocaleStore,
  useLocale,
  OutputChannels,
  filterModelsByOutputChannel,
} from 'src/modules/locale';
import { connect } from 'react-redux';
import { BaseStore } from 'src/store';
import { HeaderStore } from 'src/modules/layout';
import { GlobalIcon, IconImage, GlobalNewsletterButton } from 'src/modules/global';
import { breakpoints } from 'src/utils/breakpoints';
import { LayoutFooterMobile } from 'src/modules/layout/components/footer/LayoutFooterMobile';
import { FormattedMessage } from 'react-intl';
import { defaultHorizontalGutter } from 'src/utils/grid';

import getSocialMediaHref from 'public/config/getSocialMediaHref';

interface LayoutFooterProps {
  header: HeaderStore;
  locale: LocaleStore;
}

const LayoutFooter: React.FunctionComponent<LayoutFooterProps> = (props) => {
  const {
    locale: { locale },
  } = props;

  const {
    europe_url,
    australia_url,
    usa_url,
    getLocalizedObject,
    setLocale,
    outputChannel,
  } = useLocale();

  let articleCategories;
  if (!props.header) {
    return null;
  }

  let productCategories;
  let aboutPage;
  let professionalsPage;

  if (props.header) {
    const header = getLocalizedObject(props.header);
    productCategories = header.productCategories;
    aboutPage = header.aboutPage;
    professionalsPage = header.professionalsPage;
    articleCategories = header.articleCategories;
  } else {
    articleCategories = [];
    productCategories = [];
    aboutPage = null;
    professionalsPage = null;
  }

  const onClickLanguage = (locale) => {
    if (outputChannel === OutputChannels.DE) {
      setLocale(locale);
    }
  };

  const showPinterest = outputChannel !== OutputChannels.US;

  return (
    <Media>
      {({ currentBreakpoint } = { currentBreakpoint: 'xs' }) => {
        if (breakpoints[currentBreakpoint] < breakpoints.md) {
          return (
            <LayoutFooterMobile
              key="footer-mobile"
              productCategories={productCategories}
              articleCategories={articleCategories}
              aboutPage={aboutPage}
              professionalsPage={professionalsPage}
              setLocale={onClickLanguage}
              locale={locale}
              usaUrl={usa_url}
              australiaUrl={australia_url}
              europeUrl={europe_url}
            />
          );
        }

        return (
          <footer key="footer-desktop" className={styles.container}>
            <Logo size="large" className={styles.logo} />
            <Row className={styles.primaryLinksContainer} gutter={[defaultHorizontalGutter, 0]}>
              <Col span={4}>
                <div className={styles.sectionTitle}>
                  <FormattedMessage id="header/menu_button/products" />
                </div>
                {productCategories.map((pc) => (
                  <div key={`product-category-${pc.id}`}>
                    <Link href={`/products/${encodeURIComponent(pc.slug)}`}>
                      <a className={styles.link}>{pc.name}</a>
                    </Link>
                  </div>
                ))}
              </Col>
              <Col span={4}>
                <div className={styles.sectionTitle}>
                  <FormattedMessage id="header/menu_button/about" />
                </div>
                {aboutPage?.dynamic_content.map((dc, idx) => (
                  <div key={`about-link-${idx}`}>
                    <Link href={`/about/${encodeURIComponent(dc.link)}`}>
                      <a className={styles.link}>{dc.title}</a>
                    </Link>
                  </div>
                ))}
              </Col>
              <Col span={4}>
                <div className={styles.sectionTitle}>
                  <FormattedMessage id="header/menu_button/professionals" />
                </div>
                {(filterModelsByOutputChannel(
                  professionalsPage?.dynamic_content,
                  outputChannel
                ) as any).map((dc, idx) => (
                  <div key={`professionals-link-${idx}`}>
                    <Link href={`/professionals/${encodeURIComponent(dc.link)}`}>
                      <a className={styles.link}>{dc.title}</a>
                    </Link>
                  </div>
                ))}
              </Col>
              <Col span={4}>
                <div className={styles.inspirationContainer}>
                  <div className={styles.sectionTitle}>
                    <FormattedMessage id="footer/inspiration" />
                  </div>
                  <div>
                    <Link href={'/magazine'}>
                      <a className={styles.link}>
                        <FormattedMessage id="page/magazine/listing/all_topics" />
                      </a>
                    </Link>
                  </div>
                  {articleCategories.map((ac, idx) => (
                    <div key={idx}>
                      <Link href={`/magazine?category=${encodeURIComponent(ac.name)}`}>
                        <a className={styles.link}>{ac.name}</a>
                      </Link>
                    </div>
                  ))}
                  <div>
                    <Link href={'/magazine/instagram'}>
                      <a className={styles.link}>
                        <FormattedMessage id="breadcrumbs/magazine/instagram" />
                      </a>
                    </Link>
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <GlobalNewsletterButton showInfoText={true} />
              </Col>
            </Row>
            <div className={styles.secondaryLinksContainer}>
              <Row className={styles.secondaryLinksInnerContainer}>
                <Col span={12} className={styles.socialIconsContainer}>
                  <div className={styles.label}>
                    <FormattedMessage id="footer/follow_us" />
                  </div>
                  <a href={getSocialMediaHref(locale, 'facebook')} className={styles.socialLink}>
                    <GlobalIcon image={IconImage.Facebook} />
                  </a>
                  <a href={getSocialMediaHref(locale, 'linkedin')} className={styles.socialLink}>
                    <GlobalIcon image={IconImage.Linkedin} />
                  </a>
                  <a href={getSocialMediaHref(locale, 'instagram')} className={styles.socialLink}>
                    <GlobalIcon image={IconImage.Instagram} />
                  </a>
                  <a href={getSocialMediaHref(locale, 'youtube')} className={styles.socialLink}>
                    <GlobalIcon image={IconImage.Youtube} />
                  </a>
                  {showPinterest && (
                    <a href={getSocialMediaHref(locale, 'pinterest')} className={styles.socialLink}>
                      <GlobalIcon image={IconImage.Pinterest} />
                    </a>
                  )}
                </Col>
              </Row>
              <div className={styles.secondaryLinksContainer}>
                <Row className={styles.secondaryLinksInnerContainer}>
                  <Col span={12} className={styles.legalContainer}>
                    <Link href="/imprint">
                      <a className={styles.legalLink}>
                        <FormattedMessage id="footer/imprint" />
                      </a>
                    </Link>
                    <Link href="/terms">
                      <a className={styles.legalLink}>
                        <FormattedMessage id="footer/terms" />
                      </a>
                    </Link>
                    <Link href="/privacy">
                      <a className={styles.legalLink}>
                        <FormattedMessage id="footer/privacy" />
                      </a>
                    </Link>
                  </Col>
                  <Col span={12} className={styles.copyrightContainer}>
                    <p className={styles.info}>
                      <FormattedMessage id="footer/copyright" />
                    </p>
                  </Col>
                </Row>
              </div>
            </div>
          </footer>
        );
      }}
    </Media>
  );
};

const mapStateToProps = (state: BaseStore) => {
  return {
    header: state.header,
    locale: state.locale,
  };
};

export default connect(mapStateToProps)(LayoutFooter);
